import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const joinUrl = (base, path) => {
  if (!base) return path;
  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

const robustFetch = async (url, options = {}, retries = 2) => {
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      ...options,
      headers: {
        ...options.headers,
      }
    });
    
    if (!response.ok) {
      let errorDetail = response.statusText;
      try {
        const data = await response.json();
        errorDetail = data.detail || data.message || JSON.stringify(data);
      } catch (e) {}
      throw new Error(`HTTP ${response.status}: ${errorDetail}`);
    }
    return response;
  } catch (err) {
    if (retries > 0 && err.name !== 'AbortError') {
      console.warn(`Fetch failed, retrying... (${retries} left). Error: ${err.message}`);
      await new Promise(res => setTimeout(res, 1000));
      return robustFetch(url, options, retries - 1);
    }
    throw err;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString();
};

function SqlOptimization() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedJobLogs, setSelectedJobLogs] = useState(null);
  const [selectedJobLoading, setSelectedJobLoading] = useState(false);

  const [config] = useState(() => {
    const savedConfig = localStorage.getItem('scada_api_config');
    const parsed = savedConfig ? JSON.parse(savedConfig) : {};
    return {
      apiBase: parsed.apiUrl || process.env.REACT_APP_API_BASE || 'http://localhost:20021/api',
      apiKey: parsed.apikey !== undefined ? parsed.apikey : (process.env.REACT_APP_API_KEY || '')
    };
  });

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await robustFetch(joinUrl(config.apiBase, 'logs/?days=2'), {
        method: 'GET',
        headers: { 'apikey': config.apiKey },
      });
      const data = await response.json();
      const sqlLogs = data.logs ? data.logs.filter(l => l.agent_type === 'sql_optimization') : [];
      setHistory(sqlLogs);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadJobDetails = async (jobId) => {
    setSelectedJobLoading(true);
    setSelectedJobLogs(null);
    try {
      const response = await robustFetch(joinUrl(config.apiBase, `logs/${jobId}`), {
        method: 'GET',
        headers: { 'apikey': config.apiKey },
      });
      const data = await response.json();
      setSelectedJobLogs(data.logs);
    } catch (err) {
      console.error('Failed to load job details:', err);
    } finally {
      setSelectedJobLoading(false);
    }
  };

  const handleShowHistory = async () => {
    setShowHistory(true);
    await loadHistory();
  };

  const handleJobClick = async (jobId) => {
    setSelectedJob(jobId);
    await loadJobDetails(jobId);
  };

  const handleCloseDetails = () => {
    setSelectedJob(null);
    setSelectedJobLogs(null);
  };

  useEffect(() => {
    if (showHistory && history.length === 0 && !historyLoading) {
      loadHistory();
    }
  }, [showHistory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setError('Please enter SQL code to optimize');
      return;
    }
    
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await robustFetch(joinUrl(config.apiBase, `sql_optimization?code=${encodeURIComponent(code)}`), {
        method: 'POST',
        headers: { 'apikey': config.apiKey },
        mode: 'cors'
      });

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setCode('');
    setResult(null);
    setError(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const renderCodeBlock = (title, content, color = '#1e293b') => {
    if (!content) return null;
    return (
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '0.5rem'
        }}>
          <span style={{ fontWeight: '600', color: color, fontSize: '0.9rem' }}>
            {title}
          </span>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={() => copyToClipboard(content)}
          >
            Copy
          </button>
        </div>
        <pre style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '1rem',
          overflow: 'auto',
          maxHeight: '300px',
          fontFamily: "'Courier New', monospace",
          fontSize: '0.85rem',
          lineHeight: '1.6',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          margin: 0
        }}>
          {content}
        </pre>
      </div>
    );
  };

  const renderHistory = () => {
    if (historyLoading) {
      return (
        <div className="loading">
          <div className="spinner"></div>
          Loading history...
        </div>
      );
    }

    if (history.length === 0) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
          No recent conversions found
        </div>
      );
    }

    return (
      <div style={{ marginTop: '1rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Job ID</th>
              <th style={{ textAlign: 'left', padding: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Agent Type</th>
              <th style={{ textAlign: 'left', padding: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Date</th>
              <th style={{ textAlign: 'left', padding: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {history.map((log, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.75rem', fontSize: '0.85rem', fontFamily: 'monospace' }}>
                  {log.job_id}
                </td>
                <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                  {log.agent_type}
                </td>
                <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                  {formatDate(log.created_at)}
                </td>
                <td style={{ padding: '0.75rem' }}>
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleJobClick(log.job_id)}
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderJobDetails = () => {
    if (!selectedJob) return null;

    if (selectedJobLoading) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <div className="spinner"></div>
          Loading details...
        </div>
      );
    }

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }} onClick={handleCloseDetails}>
        <div 
          style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '1.5rem',
            maxWidth: '900px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3>Optimization Details: {selectedJob}</h3>
            <button className="btn btn-secondary" onClick={handleCloseDetails}>Close</button>
          </div>
          
          {selectedJobLogs && selectedJobLogs.map((log, idx) => (
            <div key={idx} style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <strong>{log.step}</strong>
                <span style={{ 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '4px', 
                  fontSize: '0.75rem',
                  background: log.status === 'completed' ? '#dcfce7' : '#fef3c7',
                  color: log.status === 'completed' ? '#166534' : '#92400e'
                }}>
                  {log.status}
                </span>
              </div>
              
              {log.input_text && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.25rem' }}>Input:</div>
                  <pre style={{ 
                    background: '#fff', 
                    padding: '0.75rem', 
                    borderRadius: '4px', 
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                    overflow: 'auto',
                    maxHeight: '150px',
                    whiteSpace: 'pre-wrap',
                  }}>
                    {log.input_text}
                  </pre>
                </div>
              )}
              
              {log.output_text && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.25rem' }}>Output:</div>
                  <pre style={{ 
                    background: '#fff', 
                    padding: '0.75rem', 
                    borderRadius: '4px', 
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                    overflow: 'auto',
                    maxHeight: '150px',
                    whiteSpace: 'pre-wrap',
                  }}>
                    {log.output_text}
                  </pre>
                </div>
              )}
              
              {log.review_comment && (
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.25rem' }}>Review Comment:</div>
                  <div style={{ background: '#fff', padding: '0.75rem', borderRadius: '4px', fontSize: '0.8rem' }}>
                    {log.review_comment}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="back-button" onClick={() => navigate('/')}>
        ← Back to Home
      </div>

      <div className="page-header">
        <h2 className="page-title">SQL Optimization Agent</h2>
        <p className="page-subtitle">
          Optimize and convert SQL code for Ignition
        </p>
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'row', gap: '2rem' }}>
        <div style={{ flex: 1 }}>
          <h3 className="card-title">Input SQL Code</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <textarea
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Paste your SQL code here..."
              />
            </div>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Optimizing...' : 'Optimize SQL'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleClear}>
                Clear
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleShowHistory}>
                View History
              </button>
            </div>
          </form>
        </div>
      </div>

      {showHistory && !selectedJob && (
        <div className="card">
          <h3 className="card-title">Recent Optimizations (Last 2 Days)</h3>
          {renderHistory()}
        </div>
      )}

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          Optimizing SQL...
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <div className="alert-content">
            <div className="alert-title">Error</div>
            <div>{error}</div>
          </div>
        </div>
      )}

      {result && (
        <div className="card">
          <h3 className="card-title">Optimization Results</h3>
          
          {result.job_id && (
            <div style={{ 
              background: '#f0fdf4', 
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              padding: '0.75rem',
              marginBottom: '1rem',
              fontSize: '0.85rem',
              color: '#166534'
            }}>
              <strong>Job ID:</strong> {result.job_id}
            </div>
          )}

          {result.existing_code && (
            <div style={{ 
              background: '#fef3c7', 
              border: '1px solid #fcd34d',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem'
            }}>
              <strong style={{ color: '#92400e' }}>Input SQL:</strong>
              <pre style={{ 
                marginTop: '0.5rem', 
                whiteSpace: 'pre-wrap',
                fontSize: '0.85rem',
                color: '#78350f'
              }}>
                {result.existing_code}
              </pre>
            </div>
          )}

          {renderCodeBlock('Optimized SQL', result.converted_code, '#10b981')}
          {renderCodeBlock('Reviewed SQL', result.reviewed_code, '#6366f1')}
          
          {result.review_comment && (
            <div style={{ 
              background: '#eff6ff', 
              border: '1px solid #93c5fd',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <strong style={{ color: '#1e40af' }}>Review Comments:</strong>
              <p style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>
                {result.review_comment}
              </p>
            </div>
          )}

          {!result.converted_code && !result.reviewed_code && !result.existing_code && (
            <div className="result-box">
              {JSON.stringify(result, null, 2)}
            </div>
          )}
        </div>
      )}

      {renderJobDetails()}
    </div>
  );
}

export default SqlOptimization;